import React, { useCallback, useEffect, useState } from "react";

import { disableFieldModulesByFieldId, getFieldShapesAdmin } from "services";

import { formatNumber, useCancelAsyncOnUnmount } from "utils";

import {
  AdminDescriptions,
  AdminSection,
  Card,
  Col,
  Collapse,
  MapInteractive,
  Modal,
  Panel,
  Row,
  Tabs,
  TagNormal,
  Tooltip,
  Typography,
  Message as message,
} from "components";

import { FarmFieldSocSamples } from "pages/admin/farms/components/FarmFieldSocSamples";

export const FarmFieldsTab = ({ farm, location, markers, loadFarm }) => {
  const [loading, setLoading] = useState(false);
  const [geometries, setGeometries] = useState([]);
  const signal = useCancelAsyncOnUnmount()();
  const [showModal, setShowModal] = useState(false);
  const [fieldIdToDisable, setFieldIdToDisable] = useState(null);
  const [disablingFieldModules, setDisablingFieldModules] = useState(false);

  const getShapes = useCallback(() => {
    getFieldShapesAdmin({
      farmId: farm.id,
      signal,
    })
      .then((res) => {
        setGeometries(res.fieldShapes);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [farm.id, signal]);

  useEffect(() => {
    getShapes();
  }, [farm.zip, getShapes]);

  const handleModalClose = () => {
    setShowModal(false);
    setFieldIdToDisable(null);
  };

  const disableFieldModules = () => {
    setDisablingFieldModules(true);

    disableFieldModulesByFieldId({
      fieldId: fieldIdToDisable,
      signal,
    })
      .then(() => {
        message({
          content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
          icon: "gremlin",
        });
        loadFarm(false);
        handleModalClose();
      })
      .finally(() => setDisablingFieldModules(false));
  };

  return (
    !loading && (
      <>
        {location && (
          <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]}>
            <Col>
              <Card className="map-card">
                <MapInteractive
                  geometries={geometries}
                  location={location}
                  markers={[
                    ...markers,
                    {
                      location: location,
                      name: farm.name,
                    },
                  ]}
                  scaleControl={true}
                  zoomControl={true}
                />
              </Card>
            </Col>
          </Row>
        )}
        {farm && farm.fieldList && farm.fieldList.length > 0 ? (
          <Collapse className="margin-bottom-300">
            {farm.fieldList.map((field, i) => (
              <Panel
                header={
                  <>
                    {field.name ? field.name : "Unknown field name"}
                    <Typography variant="paragraph-m" color="grey-600" className="small margin-left-100">
                      {formatNumber({ number: field.size, decimalPlaces: 4 })} ha{" "}
                      {field.modules.length > 0 && `| Modules: ${field.moduleStates.active} active - ${field.moduleStates.disabled} disabled`}
                    </Typography>
                  </>
                }
                key={i}
                id={`#${field.id}`}
                extra={
                  field.verified ? (
                    <Tooltip placement="top" title="This field is verified">
                      <span>
                        <TagNormal color="green" text="Verified" />
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip placement="top" title="This field is not verified">
                      <span>
                        <TagNormal color="red" text="Unverified" />
                      </span>
                    </Tooltip>
                  )
                }
              >
                <Tabs
                  items={[
                    {
                      label: "Field details",
                      key: "field",
                      children: (
                        <>
                          <AdminDescriptions
                            items={[
                              {
                                span: 1,
                                label: "Field number",
                                children: field.number ? field.number : "-",
                              },
                              {
                                span: 1,
                                label: "Field size (ha)",
                                children: field.size ? formatNumber({ number: field.size, decimalPlaces: 4 }) : "-",
                              },
                              {
                                span: 1,
                                label: "Field location",
                                children: field.location ? `${field.location.lat} (lat), ${field.location.lng} (lng)` : "-",
                              },
                              {
                                span: 1,
                                label: "Nearby",
                                children:
                                  field.nearby.length > 0
                                    ? field.nearby.map((item, i) => (
                                        <Typography variant="paragraph-m" key={i}>
                                          {item}{" "}
                                        </Typography>
                                      ))
                                    : "-",
                              },
                              {
                                span: 1,
                                label: "Soil type",
                                children: field.soilType ? field.soilType : "-",
                              },
                              {
                                span: 1,
                                label: "Soil points",
                                children: field.soilPoints ? field.soilPoints : "-",
                              },
                              {
                                span: 1,
                                label: "Field ID",
                                children: field.id,
                              },
                            ]}
                          />

                          <FarmFieldSocSamples fieldId={field.id} className="margin-top-200" />
                        </>
                      ),
                    },
                  ]}
                />
              </Panel>
            ))}
          </Collapse>
        ) : (
          <AdminSection description="No methods yet :(" />
        )}
        <Modal
          cancelText="close"
          description={"Are you sure you want to disable all modules on this field? All Vesting, if exists, will be lost."}
          onOk={disableFieldModules}
          onCancel={handleModalClose}
          okText={"proceed"}
          showModal={showModal}
          title={"Disable field modules"}
          loading={disablingFieldModules}
        />
      </>
    )
  );
};
